import type { Locale } from '@shop/types';
import { useTranslation } from 'react-i18next';

import { Field } from '../../components/ui/field';
import { Checkbox, Input, Select } from '../../components/ui/input';
import { pickTranslation } from '../../lib/queries';
import type { Attribute } from '../../lib/types';
import { type AttributeDraft, emptyDraft } from './attribute-values';

/** Поля характеристик товара по типу атрибута. Локализованный текст — на выбранном языке. */
export function AttributesForm({
  attributes,
  drafts,
  onChange,
  locale,
  disabled,
}: {
  attributes: Attribute[];
  drafts: Record<string, AttributeDraft>;
  onChange: (next: Record<string, AttributeDraft>) => void;
  locale: Locale;
  disabled?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const update = (id: string, patch: Partial<AttributeDraft>) => {
    onChange({ ...drafts, [id]: { ...(drafts[id] ?? emptyDraft()), ...patch } });
  };

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {attributes.map((a) => {
        const d = drafts[a.id] ?? emptyDraft();
        const label = `${a.names[i18n.language as Locale] ?? pickTranslation(a.names, i18n.language) ?? a.code}${a.unit ? `, ${a.unit}` : ''}`;
        const optionLabel = (o: Attribute['options'][number]) =>
          pickTranslation(o.labels, i18n.language) ?? o.code;
        return (
          <Field key={a.id} label={label}>
            {(id) => {
              switch (a.type) {
                case 'text': {
                  const key: Locale | '' = a.isLocalized ? locale : '';
                  return (
                    <Input
                      id={id}
                      disabled={disabled}
                      value={d.text[key] ?? ''}
                      onChange={(e) => {
                        update(a.id, { text: { ...d.text, [key]: e.target.value } });
                      }}
                    />
                  );
                }
                case 'number':
                case 'dimension':
                  return (
                    <Input
                      id={id}
                      inputMode="decimal"
                      disabled={disabled}
                      value={d.number}
                      onChange={(e) => {
                        update(a.id, { number: e.target.value });
                      }}
                    />
                  );
                case 'bool':
                  return (
                    <Select
                      id={id}
                      disabled={disabled}
                      value={d.bool}
                      onChange={(e) => {
                        update(a.id, { bool: e.target.value as AttributeDraft['bool'] });
                      }}
                    >
                      <option value="">{t('common.none')}</option>
                      <option value="true">{t('common.yes')}</option>
                      <option value="false">{t('common.no')}</option>
                    </Select>
                  );
                case 'enum':
                  return (
                    <Select
                      id={id}
                      disabled={disabled}
                      value={d.optionIds[0] ?? ''}
                      onChange={(e) => {
                        update(a.id, { optionIds: e.target.value ? [e.target.value] : [] });
                      }}
                    >
                      <option value="">{t('common.none')}</option>
                      {a.options.map((o) => (
                        <option key={o.id} value={o.id}>
                          {optionLabel(o)}
                        </option>
                      ))}
                    </Select>
                  );
                case 'multi_enum':
                  return (
                    <div id={id} className="flex flex-wrap gap-3 rounded-md border p-2">
                      {a.options.map((o) => (
                        <label key={o.id} className="flex items-center gap-1.5 text-sm">
                          <Checkbox
                            disabled={disabled}
                            checked={d.optionIds.includes(o.id)}
                            onChange={(e) => {
                              update(a.id, {
                                optionIds: e.target.checked
                                  ? [...d.optionIds, o.id]
                                  : d.optionIds.filter((x) => x !== o.id),
                              });
                            }}
                          />
                          {optionLabel(o)}
                        </label>
                      ))}
                    </div>
                  );
              }
            }}
          </Field>
        );
      })}
    </div>
  );
}
